import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import PageHeader from "../../components/PageHeader";
import { Spinner } from "react-bootstrap";
import infoCircle from "../../assets/info-circle.svg";
import TinyMceEditor from "../../components/TinyMceEditor";
import TagInput from "../../components/TagInput";
import MediaPickerModal from "../../components/MediaPickerModal";
import OGFields from "../../components/pages/OGTagsFields";
import { toast } from "react-toastify";
import { requestWithToken } from "../../utils/apis";
import { fetchBlogCategoryList } from "../../action/formaction";
import { blogDetail } from "../../action/blogs";

const CreateLocalizedBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const selectedLang = searchParams.get("lang");

  const [banner, setBanner] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showOGMediaPicker, setShowOGMediaPicker] = useState(false);
  const [categories, setCategories] = useState([]);
  const editorRef = useRef(null);
  const rightSectionRef = useRef(null);
  const [loadingButton, setLoadingButton] = useState(null);
  const [authorImage, setAuthorImage] = useState(null); 
  const [showImagePicker, setShowImagePicker] = useState(false);

  const handlePublishToggle = async () => {
    setLoadingButton("toggle");
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      // No validation errors → safe to toggle publish
      formik.setFieldValue("is_published", !formik?.values?.is_published);
      await formik.submitForm(); // same as handleSubmit but returns a Promise
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      setLoadingButton(null); // stop loader if invalid
    }
  };

  const handleSaveOrUpdate = async () => {
    setLoadingButton("save");
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      setLoadingButton(null); // stop loader if invalid
    }
  };

   const fetchDetails = async () => {
      try {
        const response = await blogDetail(id);
        if (response?.status) {
          const blog = response.data;
          formik.setFieldValue("slug", blog.slug || "");
          // setBanner(blog.assets?.url ? process.env.REACT_APP_BUCKET_URL + blog.assets.url : null);
          // setAuthorImage(blog?.extras?.author_image ? process.env.REACT_APP_BUCKET_URL + blog.extras?.author_image : null)
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
        toast.error("Failed to fetch blog details",error?.message);
      }
    };

  const formik = useFormik({
    initialValues: {
      title: "",
      slug: "",
      author: "",
      content: "",
      focus_keywords: [],
      metaTitle: "",
      metaDesc: "",
      is_published: true,
      banner: "",
      desc: "",
      og_tags: {
        title: "",
        desc: "",
        url: "",
        image: "",
      },
      publish_date: moment().format("YYYY-MM-DD"),
      blog_category_id: "",
      extras: {
        author_image:""
      },
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Blog Title is required"),
      slug: Yup.string()
        .matches(
          /^[\p{L}\p{N}_-]+$/u,
          "Slug can contain letters (any language), numbers, hyphens, and underscores"
        ),
      content: Yup.string().required("Blog Content is required"),
      banner: Yup.string().required("Banner image is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const bannerpath = banner
          ? banner.replace(process.env.REACT_APP_BUCKET_URL, "")
          : null;
          
        const payload = {
          title: values.title,
          slug: values.slug,
          content: values.content,
          author_name: values.author,
          seo: {
            focus_keywords: values.focus_keywords.join(","),
            metaTitle: values.metaTitle,
            metaDesc: values.metaDesc,
          },
          assets: banner
            ? {
                type: 1,
                url: bannerpath,
                w: "0",
                h: "0",
              }
            : {},
          is_published: values.is_published,
          publish_date: moment(values.publish_date).toISOString(),
          desc: values.desc,
          type: 1,
          og_tags: values.og_tags,
          locale: selectedLang,
          parent_id: id,
          extras: values.extras
        };
        const response = await requestWithToken("POST", "/api/blogs", payload);

        if (response?.status === 200) {
          toast.success(response.message);
          navigate("/blogs");
          resetForm();
          editorRef.current?.setContent("");
        }
      } catch (error) {
        console.error("Error submitting blog:", error);
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong. Please try again."
        );
        if (loadingButton === "toggle") {
          // revert the toggle change if error occurs during publish toggle
          formik.setFieldValue("is_published", !formik?.values?.is_published);
        }
      } finally {
        setSubmitting(false);
        setLoadingButton(null);
      }
    },
  });

  const fetchCategories = async () => {
    try {
      const response = await fetchBlogCategoryList(1, 100);
      if (response?.status) {
        console.log(response);
        setCategories(response?.data?.list);
      }
    } catch (error) {
      toast.error("Failed to fetch blog categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);
  useEffect(() => {
    if(id)fetchDetails();
  }, [id]);
  return (
    <>
      <PageHeader pageTitle={"Add New Blog"}>
        <button
          className="btn-white"
          onClick={handlePublishToggle}
          disabled={loadingButton === "toggle" && formik.isSubmitting}
        >
          {loadingButton === "toggle" && formik.isSubmitting ? (
            <Spinner size="sm" />
          ) : (
            "Save Draft"
          )}
        </button>

        <button
          className="btn-black"
          onClick={handleSaveOrUpdate}
          disabled={loadingButton === "save" && formik.isSubmitting}
        >
          {loadingButton === "save" && formik.isSubmitting ? (
            <Spinner size="sm" />
          ) : (
            "Publish"
          )}
        </button>
      </PageHeader>

      <div className="page-container">
        <div className="add-blog-form">
          <div className="row">
            {/* Left Section */}
            <div className="col-md-8 d-flex flex-column">
              <div className="theme-card text-card p-3 mb-3 h-100 ">
                {/* Blog Content (TinyEditor) */}
                <div className="frm-group h-100 d-flex flex-column mb-0 text-editor-group">
                  <div className="head-title">
                    <label className="frm-label d-flex align-items-center">
                      Blog Content
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Blog Description"
                      />
                    </label>
                  </div>
                  <div className="tinymce-wrapper">
                    <TinyMceEditor
                      value={formik.values.content}
                      onChange={(content) =>
                        formik.setFieldValue("content", content)
                      }
                      editorRef={editorRef}
                      height="100%"
                    />
                  </div>

                  {formik.touched.content && formik.errors.content && (
                    <div className="input-error">{formik.errors.content}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Section */}
            <div className="col-md-4 position-relative">
              <div className="right-sec" ref={rightSectionRef}>
                {/* General Details */}
                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">General Details</div>

                  {/* Blog Title */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Blog Title
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Blog Title"
                      />
                    </label>
                    <input
                      type="text"
                      name="title"
                      className="frm-input"
                      placeholder="Enter Blog Title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className="input-error">{formik.errors.title}</div>
                    )}
                  </div>

                  {/* Slug */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Slug
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Slug name of Blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="slug"
                      className="frm-input"
                      placeholder="Slug name eg. my-first-blog"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.slug}
                      disabled
                    />
                    {formik.touched.slug && formik.errors.slug && (
                      <div className="input-error">{formik.errors.slug}</div>
                    )}
                  </div>
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Author Name
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Author name of Blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="author"
                      className="frm-input"
                      placeholder="Author Name eg. John Doe"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.author}
                    />
                    {formik.touched.author && formik.errors.author && (
                      <div className="input-error">{formik.errors.author}</div>
                    )}
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">Author Image</label>

                    <div
                      className="pointer"
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowImagePicker(true)}
                    >
                      {authorImage ? (
                        <img
                          src={authorImage}
                          alt="image preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            background: "#f5f5f5",
                            color: "#777",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          Upload Image
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Publish Date
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Select publish date"
                      />
                    </label>
                    <input
                      type="date"
                      name="publish_date"
                      className="frm-input"
                      value={formik.values.publish_date}
                      onChange={formik.handleChange}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">Blog Banner</label>

                    <div
                      className="pointer"
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowMediaPicker(true)}
                    >
                      {banner ? (
                        <img
                          src={banner}
                          alt="banner preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            background: "#f5f5f5",
                            color: "#777",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          Upload Banner
                        </div>
                      )}
                    </div>
                    {formik.touched.banner && formik.errors.banner && (
                      <div className="input-error">{formik.errors.banner}</div>
                    )}
                  </div>
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Description
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add description of Blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="desc"
                      className="frm-input"
                      placeholder="Enter Description"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.desc}
                    />
                  </div>

                  {/* blog category */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Blog Category
                    </label>
                    <div className="sel-box ms-auto">
                      <select
                        name="blog_category_id"
                        id="selmenu"
                        className="theme-select"
                        onChange={formik.handleChange}
                        value={formik.values?.blog_category_id || ""}
                      >
                        {categories.loading ? (
                          <option>Loading...</option>
                        ) : categories.length > 0 ? (
                          <>
                            <option value="">Select Category</option>
                            {categories.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </>
                        ) : (
                          <option>No Category Found</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                {/* SEO Section */}
                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">SEO</div>

                  {/* Focus Keywords */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Focus Keywords
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Keywords"
                      />
                    </label>
                    <div className="tag-card">
                      <TagInput
                        value={formik.values.focus_keywords}
                        onChange={(tags) =>
                          formik.setFieldValue("focus_keywords", tags)
                        }
                      />
                    </div>
                  </div>

                  {/* SEO Title */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      SEO Title
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add SEO Title"
                      />
                    </label>
                    <input
                      type="text"
                      name="metaTitle"
                      className="frm-input"
                      placeholder="Enter SEO Title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.metaTitle}
                    />
                  </div>

                  {/* Meta Description */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Meta Description
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Meta Description"
                      />
                    </label>
                    <textarea
                      name="metaDesc"
                      className="frm-input"
                      rows={3}
                      placeholder="Enter Meta Description"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.metaDesc}
                    />
                  </div>
                </div>

                <OGFields
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  setFieldValue={formik.setFieldValue}
                  showMediaPicker={showOGMediaPicker}
                  setShowMediaPicker={setShowOGMediaPicker}
                />
              </div>
            </div>
            {/* End Right Section */}
          </div>
        </div>
      </div>
      {showMediaPicker && (
        <MediaPickerModal
          show={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(mediaUrl) => {
            setBanner(mediaUrl);
            formik.setFieldValue("banner", mediaUrl);
            setShowMediaPicker(false);
          }}
        />
      )}
       {showImagePicker && (
              <MediaPickerModal
                show={showImagePicker}
                onClose={() => setShowImagePicker(false)}
                onSelect={(mediaUrl) => {
                  const imagePath = mediaUrl.replace(process.env.REACT_APP_BUCKET_URL, "");
                  setAuthorImage(mediaUrl);
                  formik.setFieldValue("extras.author_image", imagePath);
                  setShowImagePicker(false);
                }}
              />
            )}
    </>
  );
};

export default CreateLocalizedBlog;
